import { useCallback, useEffect, useRef, useState } from 'react';

const GLYPHS = '!<>-_\\/[]{}=+*^?#01';

// Text that "decrypts" left to right when its closest link/row is hovered
// or focused. Pass `onMount` to also play it once on first render.
export default function Scramble({ text, onMount = false, className }) {
  const ref = useRef(null);
  const raf = useRef(0);
  const [out, setOut] = useState(text);

  const run = useCallback(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    cancelAnimationFrame(raf.current);
    const chars = Array.from(text);
    const start = performance.now();
    const duration = Math.min(650, 180 + chars.length * 28);

    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const solved = Math.floor(p * chars.length);
      setOut(
        chars
          .map((c, i) =>
            i < solved || c === ' ' ? c : GLYPHS[Math.floor(Math.random() * GLYPHS.length)]
          )
          .join('')
      );
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  }, [text]);

  useEffect(() => {
    setOut(text);
    const host = ref.current?.closest('a, button, .row') || ref.current;
    if (!host) return undefined;
    host.addEventListener('mouseenter', run);
    host.addEventListener('focus', run);
    if (onMount) run();
    return () => {
      cancelAnimationFrame(raf.current);
      host.removeEventListener('mouseenter', run);
      host.removeEventListener('focus', run);
    };
  }, [text, run, onMount]);

  return (
    <span ref={ref} className={className}>
      <span className="sr-only">{text}</span>
      <span aria-hidden="true">{out}</span>
    </span>
  );
}
